import React, { useLayoutEffect, useRef, useState } from "react";
import BuyButton from "./BuyButton";
import ProductStockStatus from "./ProductStockStatus";
import { add as addToCartService } from "../Services/Cart.service";
import { receive, toggle } from "../Actions/Cart.action";
import { useDispatch } from "react-redux";
import { translate, translateFormat } from "../Services/translation";

const ProductLink = ({ url, children }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener" className="product-detail__addon-card-link">
      {children}
    </a>
  ) : (
    children
  );

const AddonCard = ({ variant, label }) => {
  // Products sold in packs (2-pack, 4-pack, ...) start at one pack and step a whole pack at a time.
  const packSize = Math.max(1, variant.packSize || 1);
  const [quantity, setQuantity] = useState(packSize);
  const dispatch = useDispatch();

  const addToCart = async () => {
    const cart = await addToCartService({
      articleNumber: variant.sku,
      quantity: quantity,
      price: variant.price,
    });

    dispatch(receive(cart));
    dispatch(toggle());
  };

  return (
    <div className="product-detail__addon-card" key={variant.sku}>
      {variant.defaultImageUrl && (
        <ProductLink url={variant.url}>
          <img
            src={variant.defaultImageUrl}
            className="product-detail__addon-card-image"
            alt={variant.name || ''}
            width="88"
            height="88"
            loading="lazy"
            decoding="async"
          />
        </ProductLink>
      )}
      <div className="product-detail__addon-card-text-wrapper">
        {variant.brand && (
          <div className="product-detail__addon-card-brand">
            {variant.brand}
          </div>
        )}
        <div className="product-detail__addon-card-heading">
          {variant.name && (
            <div className="product-detail__addon-card-name">
              <ProductLink url={variant.url}>{variant.name}</ProductLink>
            </div>
          )}
          <div className="product-detail__addon-card-price">
            {variant.formattedCampaignPrice ? (
              <>
                <span className="product__campaign-price">
                  {variant.formattedCampaignPrice}
                </span>
                <span className="product__old-price">
                  {variant.formattedPrice}
                </span>
              </>
            ) : (
              <span className="price">{variant.formattedPrice}</span>
            )}
          </div>
        </div>
        {packSize > 1 && (
          <div className="product-detail__addon-card-pack-size">
            {translateFormat("product.soldinpack", packSize)}
          </div>
        )}
        {variant.deliveryStatus && (
          <ProductStockStatus
            status={variant.deliveryStatus}
            statusText={variant.deliveryText}
            storeStatus="False"
          />
        )}
      </div>
      <div className="product-detail__addon-card-cta-wrapper">
        <div className="product-detail__quantity-wrapper">
          <button
            type="button"
            className="product-detail__quantity-decrease"
            disabled={quantity - packSize < packSize}
            onClick={() => setQuantity(quantity - packSize)}
          >
            -
          </button>
          <input
            id={variant.sku}
            type="number"
            aria-label={translateFormat("product.quantity") || undefined}
            className="form__input product-detail__quantity-input"
            value={quantity}
            min={packSize}
            step={packSize}
            readOnly
          />
          <button
            type="button"
            className="product-detail__quantity-increase"
            disabled={quantity + packSize > 999}
            onClick={() => setQuantity(quantity + packSize)}
          >
            +
          </button>
        </div>
        <BuyButton
          label={label}
          articleNumber={variant.sku}
          quantityFieldId={variant.sku}
          price={variant.price}
          cssClass={"button buy-button product-detail__buy-button"}
          onClick={() => addToCart()}
        />
      </div>
    </div>
  );
};

const collapsedCount = 3;

const canAnimate = () =>
  "animate" in Element.prototype && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const AddonList = ({ addon, label }) => {
  const [amountToShow, setAmountToShow] = useState(collapsedCount);
  const listRef = useRef(null);
  const animation = useRef(null);
  const target = useRef(false);
  const expandFrom = useRef(null);

  const animateHeight = (from, to, done) => {
    const list = listRef.current;
    list.style.overflow = "hidden";
    const current = list.animate({ height: [`${from}px`, `${to}px`] }, { duration: 250, easing: "ease-out" });
    animation.current = current;
    const cleanup = () => {
      if (animation.current === current) {
        animation.current = null;
        list.style.overflow = "";
      }
    };
    current.onfinish = () => {
      cleanup();
      if (done) {
        done();
      }
    };
    current.oncancel = cleanup;
  };

  useLayoutEffect(() => {
    if (expandFrom.current === null) {
      return;
    }
    const from = expandFrom.current;
    expandFrom.current = null;
    animateHeight(from, listRef.current.offsetHeight);
  }, [amountToShow]);

  if (!addon.configVariants || !addon.configVariants.some((variant) => !variant.soldOut)) {
    return null;
  }

  const total = addon.configVariants.length;
  const expanded = amountToShow == total;

  const toggle = () => {
    const list = listRef.current;
    const open = animation.current ? !target.current : !expanded;
    target.current = open;

    if (!list || !canAnimate()) {
      setAmountToShow(open ? total : collapsedCount);
      return;
    }

    const from = list.getBoundingClientRect().height;
    if (animation.current) {
      animation.current.cancel();
    }

    if (open && expanded) {
      animateHeight(from, list.offsetHeight);
    } else if (open) {
      expandFrom.current = from;
      setAmountToShow(total);
    } else {
      const kept = addon.configVariants.slice(0, collapsedCount).filter((variant) => !variant.soldOut).length;
      const firstHidden = list.children[kept];
      const to = firstHidden ? firstHidden.getBoundingClientRect().top - list.getBoundingClientRect().top : from;
      animateHeight(from, to, () => setAmountToShow(collapsedCount));
    }
  };

  return (
    <div className="product-detail__addon" key={addon.name}>
      <div className="product-detail__addon-header">
        {addon.name && (
          <h2 className="product-detail__addon-title">{addon.name}</h2>
        )}
        {addon.description && (
          <div className="product-detail__addon-subtitle">
            <span className="product-detail__addon-subtitle-separator" aria-hidden="true">{" - "}</span>
            {addon.description}
          </div>
        )}
      </div>
      <div className="product-detail__addon-list" ref={listRef}>
        {addon.configVariants
          .slice(0, amountToShow)
          .filter((variant) => !variant.soldOut)
          .map((variant) => (
            <AddonCard variant={variant} key={variant.sku} label={label} />
          ))}
      </div>
      {total > collapsedCount &&
        <button
          type="button"
          className="product-detail__show-more"
          aria-expanded={expanded}
          onClick={toggle}
        >
          {translate(expanded ? "product.show-less" : "product.show-more")}
        </button>}
    </div>
  );
};

const AddonProduct = ({ addons, label }) => {
  return (
    <div className="product-detail__buy-more-wrapper">
      {addons &&
        !!addons.length &&
        addons.map((addon, idx) => (
          <AddonList addon={addon} label={label} key={idx} />
        ))}
    </div>
  );
};

export default AddonProduct;
